"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@/components/auth/Auth";
import { useNotificationsContext } from "@/components/notifications/NotificationsContext";
import WaveDropsScrollingOverlay from "@/components/waves/drops/WaveDropsScrollingOverlay";
import { UnreadDividerProvider, useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import { useWaveChatScrollOptional } from "@/contexts/wave/WaveChatScrollContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import type { Drop, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { isWaveDirectMessage } from "@/helpers/waves/wave.helpers";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import { useScrollBehavior } from "@/hooks/useScrollBehavior";
import { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import { useWaveBoostedDrops } from "@/hooks/useWaveBoostedDrops";
import { useWaveIsTyping } from "@/hooks/useWaveIsTyping";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { TweetPreviewModeProvider, type TweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { useWaveDropsClipboard } from "./hooks/useWaveDropsClipboard";
import { useDeferredNewestDrops } from "./hooks/useDeferredNewestDrops";
import { useWaveDropsNotificationRead } from "./hooks/useWaveDropsNotificationRead";
import { useWaveDropsSerialScroll } from "./hooks/useWaveDropsSerialScroll";
import { WaveDropsContent } from "./subcomponents/WaveDropsContent";
const EMPTY_DROPS: Drop[] = [];
interface WaveDropsAllProps {
  readonly waveId: string;
  readonly dropId: string | null;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly initialDrop: number | null;
  readonly dividerSerialNo?: number | null | undefined;
  readonly unreadCount?: number | undefined;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly bottomPaddingClassName?: string | undefined;
  readonly isMuted?: boolean | undefined;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
const WaveDropsAllInner: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(89);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const router = useRouter();
  const {
    removeWaveDeliveredNotifications
  } = useNotificationsContext();
  const {
    connectedProfile
  } = useAuth();
  const {
    isAppleMobile
  } = useDeviceInfo();
  const containerRef = useRef(null);
  const {
    waveMessages,
    fetchNextPage,
    waitAndRevealDrop
  } = useVirtualizedWaveDrops(waveId, dropId);
  const {
    setUnreadDividerSerialNo
  } = useUnreadDivider();
  const typingMessage = useWaveIsTyping(waveId, connectedProfile?.handle ?? null, isMuted);
  let t4;
  if ($[0] !== waveId) {
    t4 = {
      waveId
    };
    $[0] = waveId;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    data: boostedDrops
  } = useWaveBoostedDrops(t4);
  const scrollBehavior = useScrollBehavior();
  const {
    scrollContainerRef,
    scrollContainerCallbackRef,
    bottomAnchorRef,
    bottomAnchorCallbackRef,
    isAtBottom,
    shouldPinToBottom,
    scrollToVisualBottom,
    forcePinToBottom
  } = scrollBehavior;
  const t5 = Boolean(connectedProfile?.handle);
  let t6;
  if ($[2] !== removeWaveDeliveredNotifications || $[3] !== t5 || $[4] !== waveId) {
    t6 = {
      waveId,
      enabled: t5,
      removeWaveDeliveredNotifications
    };
    $[2] = removeWaveDeliveredNotifications;
    $[3] = t5;
    $[4] = waveId;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  useWaveDropsNotificationRead(t6);
  waveMessages?.drops;
  const dropsForClipboard = waveMessages?.drops ?? EMPTY_DROPS;
  let t7;
  if ($[6] !== dropsForClipboard) {
    t7 = {
      containerRef,
      drops: dropsForClipboard
    };
    $[6] = dropsForClipboard;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  useWaveDropsClipboard(t7);
  const initializedWaveRef = useRef(null);
  let t8;
  let t9;
  if ($[8] !== dividerSerialNo || $[9] !== setUnreadDividerSerialNo || $[10] !== waveId) {
    t8 = () => {
      if (initializedWaveRef.current === waveId) {
        return;
      }
      initializedWaveRef.current = waveId;
      setUnreadDividerSerialNo(dividerSerialNo ?? null);
    };
    t9 = [waveId, dividerSerialNo, setUnreadDividerSerialNo];
    $[8] = dividerSerialNo;
    $[9] = setUnreadDividerSerialNo;
    $[10] = waveId;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  if ($[13] !== isAppleMobile || $[14] !== shouldPinToBottom || $[15] !== waveId || $[16] !== waveMessages) {
    t10 = {
      waveId,
      isAppleMobile,
      waveMessages,
      shouldPinToBottom
    };
    $[13] = isAppleMobile;
    $[14] = shouldPinToBottom;
    $[15] = waveId;
    $[16] = waveMessages;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const {
    renderedWaveMessages,
    pendingDropsCount,
    revealPendingDrops: revealDeferredPendingDrops
  } = useDeferredNewestDrops(t10);
  let t11;
  if ($[18] !== dropId || $[19] !== fetchNextPage || $[20] !== initialDrop || $[21] !== renderedWaveMessages || $[22] !== scrollContainerRef || $[23] !== scrollToVisualBottom || $[24] !== shouldPinToBottom || $[25] !== waitAndRevealDrop || $[26] !== waveId || $[27] !== waveMessages) {
    t11 = {
      waveId,
      dropId,
      initialDrop,
      waveMessages,
      renderedWaveMessages,
      fetchNextPage,
      waitAndRevealDrop,
      scrollContainerRef,
      shouldPinToBottom,
      scrollToVisualBottom
    };
    $[18] = dropId;
    $[19] = fetchNextPage;
    $[20] = initialDrop;
    $[21] = renderedWaveMessages;
    $[22] = scrollContainerRef;
    $[23] = scrollToVisualBottom;
    $[24] = shouldPinToBottom;
    $[25] = waitAndRevealDrop;
    $[26] = waveId;
    $[27] = waveMessages;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const {
    serialTarget,
    queueSerialTarget,
    targetDropRef,
    isScrolling,
    scrollBaselineSerials,
    frozenAutoCollapseSerials
  } = useWaveDropsSerialScroll(t11);
  let t12;
  if ($[29] !== frozenAutoCollapseSerials || $[30] !== isScrolling || $[31] !== renderedWaveMessages?.drops || $[32] !== scrollBaselineSerials) {
    t12 = () => {
      if (!isScrolling || !scrollBaselineSerials) {
        return frozenAutoCollapseSerials;
      }
      const drops = renderedWaveMessages?.drops;
      if (!drops || drops.length === 0) {
        return frozenAutoCollapseSerials;
      }
      const next = new Set(frozenAutoCollapseSerials);
      for (const drop of drops) {
        const serialNo = drop.serial_no;
        if (typeof serialNo !== "number") {
          continue;
        }
        if (!scrollBaselineSerials.has(serialNo) && !next.has(serialNo)) {
          next.add(serialNo);
        }
      }
      return next;
    };
    $[29] = frozenAutoCollapseSerials;
    $[30] = isScrolling;
    $[31] = renderedWaveMessages?.drops;
    $[32] = scrollBaselineSerials;
    $[33] = t12;
  } else {
    t12 = $[33];
  }
  renderedWaveMessages?.drops;
  let t13;
  if ($[34] !== t12) {
    t13 = t12();
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  const autoCollapseSerials = t13;
  const waveChatScroll = useWaveChatScrollOptional();
  let t14;
  let t15;
  if ($[36] !== queueSerialTarget || $[37] !== waveChatScroll || $[38] !== waveId) {
    t14 = () => {
      if (!waveChatScroll) {
        return;
      }
      return waveChatScroll.registerScrollHandler({
        waveId,
        handler: queueSerialTarget
      });
    };
    t15 = [waveChatScroll, waveId, queueSerialTarget];
    $[36] = queueSerialTarget;
    $[37] = waveChatScroll;
    $[38] = waveId;
    $[39] = t14;
    $[40] = t15;
  } else {
    t14 = $[39];
    t15 = $[40];
  }
  useEffect(t14, t15);
  let t16;
  if ($[41] !== forcePinToBottom || $[42] !== revealDeferredPendingDrops || $[43] !== waveMessages?.drops?.length) {
    t16 = () => {
      if (!waveMessages?.drops?.length) {
        return;
      }
      revealDeferredPendingDrops();
      forcePinToBottom();
    };
    $[41] = forcePinToBottom;
    $[42] = revealDeferredPendingDrops;
    $[43] = waveMessages?.drops?.length;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  const revealPendingDrops = t16;
  let t17;
  if ($[45] !== dropId || $[46] !== fetchNextPage || $[47] !== waveId || $[48] !== waveMessages?.hasNextPage || $[49] !== waveMessages?.isLoading || $[50] !== waveMessages?.isLoadingNextPage) {
    t17 = async () => {
      if (waveMessages?.hasNextPage && !waveMessages?.isLoading && !waveMessages?.isLoadingNextPage) {
        await fetchNextPage({
          waveId,
          type: DropSize.FULL
        }, dropId);
      }
    };
    $[45] = dropId;
    $[46] = fetchNextPage;
    $[47] = waveId;
    $[48] = waveMessages?.hasNextPage;
    $[49] = waveMessages?.isLoading;
    $[50] = waveMessages?.isLoadingNextPage;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  waveMessages?.hasNextPage;
  waveMessages?.isLoading;
  waveMessages?.isLoadingNextPage;
  const handleTopIntersection = t17;
  let t18;
  if ($[52] !== queueSerialTarget || $[53] !== router || $[54] !== waveId) {
    t18 = drop_0 => {
      if (drop_0.wave.id === waveId) {
        queueSerialTarget(drop_0.serial_no);
      } else {
        const waveDetails = drop_0.wave as unknown as {
          chat?: {
            scope?: {
              group?: {
                is_direct_message?: boolean | undefined;
              } | undefined;
            } | undefined;
          } | undefined;
        } ?? undefined;
        const isDirectMessage = isWaveDirectMessage(drop_0.wave.id, waveDetails);
        const href = getWaveRoute({
          waveId: drop_0.wave.id,
          serialNo: drop_0.serial_no,
          isDirectMessage,
          isApp: false
        });
        router.push(href);
      }
    };
    $[52] = queueSerialTarget;
    $[53] = router;
    $[54] = waveId;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  const handleQuoteClick = t18;
  const t19 = isScrolling || serialTarget !== null;
  let t20;
  if ($[56] !== activeDrop || $[57] !== autoCollapseSerials || $[58] !== boostedDrops || $[59] !== bottomAnchorCallbackRef || $[60] !== bottomAnchorRef || $[61] !== bottomPaddingClassName || $[62] !== dropId || $[63] !== forcePinToBottom || $[64] !== handleQuoteClick || $[65] !== handleTopIntersection || $[66] !== isAtBottom || $[67] !== isVotingClosed || $[68] !== isVotingControlsLocked || $[69] !== onDropContentClick || $[70] !== onReply || $[71] !== pendingDropsCount || $[72] !== queueSerialTarget || $[73] !== renderedWaveMessages || $[74] !== revealPendingDrops || $[75] !== scrollContainerCallbackRef || $[76] !== scrollContainerRef || $[77] !== serialTarget || $[78] !== t19 || $[79] !== targetDropRef || $[80] !== typingMessage || $[81] !== unreadCount || $[82] !== winningThreshold) {
    t20 = <TweetPreviewModeProvider mode="never"><WaveDropsContent waveMessages={renderedWaveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} scrollContainerCallbackRef={scrollContainerCallbackRef} bottomAnchorRef={bottomAnchorRef} bottomAnchorCallbackRef={bottomAnchorCallbackRef} onTopIntersection={handleTopIntersection} onReply={onReply} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={handleQuoteClick} isAtBottom={isAtBottom} scrollToBottom={forcePinToBottom} typingMessage={typingMessage} onDropContentClick={onDropContentClick} pendingCount={pendingDropsCount} onRevealPending={revealPendingDrops} bottomPaddingClassName={bottomPaddingClassName} boostedDrops={boostedDrops} onBoostedDropClick={queueSerialTarget} onScrollToUnread={queueSerialTarget} unreadCount={unreadCount} autoCollapseSerials={autoCollapseSerials} suspendLightDropHydration={t19} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></TweetPreviewModeProvider>;
    $[56] = activeDrop;
    $[57] = autoCollapseSerials;
    $[58] = boostedDrops;
    $[59] = bottomAnchorCallbackRef;
    $[60] = bottomAnchorRef;
    $[61] = bottomPaddingClassName;
    $[62] = dropId;
    $[63] = forcePinToBottom;
    $[64] = handleQuoteClick;
    $[65] = handleTopIntersection;
    $[66] = isAtBottom;
    $[67] = isVotingClosed;
    $[68] = isVotingControlsLocked;
    $[69] = onDropContentClick;
    $[70] = onReply;
    $[71] = pendingDropsCount;
    $[72] = queueSerialTarget;
    $[73] = renderedWaveMessages;
    $[74] = revealPendingDrops;
    $[75] = scrollContainerCallbackRef;
    $[76] = scrollContainerRef;
    $[77] = serialTarget;
    $[78] = t19;
    $[79] = targetDropRef;
    $[80] = typingMessage;
    $[81] = unreadCount;
    $[82] = winningThreshold;
    $[83] = t20;
  } else {
    t20 = $[83];
  }
  let t21;
  if ($[84] !== isScrolling) {
    t21 = <WaveDropsScrollingOverlay isVisible={isScrolling} />;
    $[84] = isScrolling;
    $[85] = t21;
  } else {
    t21 = $[85];
  }
  let t22;
  if ($[86] !== t20 || $[87] !== t21) {
    t22 = <div ref={containerRef} className="tw-relative tw-flex tw-h-full tw-flex-col tw-justify-end tw-overflow-hidden tw-bg-iron-950">{t20}{t21}</div>;
    $[86] = t20;
    $[87] = t21;
    $[88] = t22;
  } else {
    t22 = $[88];
  }
  return t22;
};
export const WaveDropsAllWithoutProvider: React.FC<WaveDropsAllProps> = WaveDropsAllInner;
const WaveDropsAll: React.FC<WaveDropsAllProps> = t0 => {
  const $ = _c(18);
  const {
    waveId,
    dropId,
    onReply,
    activeDrop,
    initialDrop,
    dividerSerialNo,
    unreadCount,
    onDropContentClick,
    bottomPaddingClassName,
    isMuted: t1,
    winningThreshold,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const isMuted = t1 === undefined ? false : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const t4 = dividerSerialNo ?? null;
  const t5 = `unread-divider-${waveId}`;
  let t6;
  if ($[0] !== activeDrop || $[1] !== bottomPaddingClassName || $[2] !== dividerSerialNo || $[3] !== dropId || $[4] !== initialDrop || $[5] !== isMuted || $[6] !== isVotingClosed || $[7] !== isVotingControlsLocked || $[8] !== onDropContentClick || $[9] !== onReply || $[10] !== unreadCount || $[11] !== waveId || $[12] !== winningThreshold) {
    t6 = <WaveDropsAllInner key={waveId} waveId={waveId} dropId={dropId} onReply={onReply} activeDrop={activeDrop} initialDrop={initialDrop} dividerSerialNo={dividerSerialNo} unreadCount={unreadCount} onDropContentClick={onDropContentClick} bottomPaddingClassName={bottomPaddingClassName} isMuted={isMuted} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} />;
    $[0] = activeDrop;
    $[1] = bottomPaddingClassName;
    $[2] = dividerSerialNo;
    $[3] = dropId;
    $[4] = initialDrop;
    $[5] = isMuted;
    $[6] = isVotingClosed;
    $[7] = isVotingControlsLocked;
    $[8] = onDropContentClick;
    $[9] = onReply;
    $[10] = unreadCount;
    $[11] = waveId;
    $[12] = winningThreshold;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t4 || $[15] !== t5 || $[16] !== t6) {
    t7 = <UnreadDividerProvider initialSerialNo={t4} key={t5}>{t6}</UnreadDividerProvider>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
};
export default WaveDropsAll;